import React, { useRef, useState } from "react";
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, X, FileDown } from "lucide-react";
import { api, formatError } from "../lib/api.js";

/**
 * Excel import widget for the PCP classified-parts table.
 *
 * Renders two buttons (Download template / Import Excel) and orchestrates the
 * validate → preview → commit flow. The parent component must pass:
 *  - pcp: the PCP record (needs id, number, status)
 *  - onImported(count): callback fired after a successful commit so the parent
 *    can refresh the classified_parts table.
 */
export default function PcpExcelImport({ pcp, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState(null); // {errors:[], total}
  const [preview, setPreview] = useState(null); // {rows, total}
  const [committing, setCommitting] = useState(false);
  const [successToast, setSuccessToast] = useState("");

  const closed = pcp.status === "Encerrado" || pcp.status === "Rejeitado";
  const closedTooltip = "Este PCP está encerrado e não aceita novas peças.";
  const partsCount = (pcp.classified_parts || []).length;

  const downloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get(`/classification-requests/${pcp.id}/parts-template`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template_pcp_${pcp.number}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatError(e?.response?.data?.detail) || "Falha ao descarregar o template.");
    }
  };

  const exportParts = async () => {
    setError("");
    try {
      const res = await api.get(`/classification-requests/${pcp.id}/parts-export`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pecas_pcp_${pcp.number}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatError(e?.response?.data?.detail) || "Falha ao exportar as peças.");
    }
  };

  const onPickFile = () => {
    if (closed) return;
    setError("");
    setValidationErrors(null);
    setPreview(null);
    fileRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of same file
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      setError("Apenas ficheiros .xlsx ou .xls são aceites.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/classification-requests/${pcp.id}/parts-import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (data.status === "errors") {
        setValidationErrors(data);
      } else if (data.status === "preview") {
        setPreview(data);
      }
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || "Falha ao processar o ficheiro.");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    setError("");
    try {
      // Drop the original spreadsheet 'row' field before commit
      const rows = preview.rows.map(({ row, ...rest }) => rest);
      const { data } = await api.post(
        `/classification-requests/${pcp.id}/parts-import/commit`,
        { rows },
      );
      setPreview(null);
      setSuccessToast(`${data.imported} linhas importadas com sucesso.`);
      setTimeout(() => setSuccessToast(""), 4000);
      onImported?.(data.imported);
    } catch (e) {
      setError(formatError(e?.response?.data?.detail) || "Falha ao importar.");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="pcp-excel-import">
      <button
        onClick={downloadTemplate}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 hover:border-[#1F3864] hover:bg-[#F4F6F8] text-[#1F3864] rounded-sm text-xs font-medium"
        data-testid="pcp-template-download-btn"
      >
        <Download className="w-3.5 h-3.5" />
        Descarregar template Excel
      </button>
      <button
        onClick={exportParts}
        disabled={partsCount === 0}
        title={partsCount === 0 ? "Sem peças classificadas para exportar." : undefined}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 hover:border-[#1F3864] hover:bg-[#F4F6F8] text-[#1F3864] rounded-sm text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:bg-transparent"
        data-testid="pcp-excel-export-btn"
      >
        <FileDown className="w-3.5 h-3.5" />
        Exportar peças para Excel
      </button>
      <button
        onClick={onPickFile}
        disabled={closed || busy}
        title={closed ? closedTooltip : undefined}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#1F3864] hover:bg-[#142542] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white rounded-sm text-xs font-medium"
        data-testid="pcp-excel-import-btn"
      >
        <Upload className="w-3.5 h-3.5" />
        {busy ? "A processar…" : "Importar peças do Excel"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={onFileSelected}
        data-testid="pcp-excel-file-input"
      />

      {error && (
        <div className="basis-full mt-2 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-xs flex items-start gap-2" data-testid="pcp-excel-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successToast && (
        <div className="basis-full mt-2 px-3 py-2 bg-[#D4EDDA] text-[#155724] border border-[#28A745]/30 rounded-sm text-xs flex items-center gap-2" data-testid="pcp-excel-success">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {successToast}
        </div>
      )}

      {validationErrors && (
        <ValidationErrorsDialog
          data={validationErrors}
          onClose={() => setValidationErrors(null)}
        />
      )}

      {preview && (
        <PreviewDialog
          data={preview}
          committing={committing}
          onCancel={() => setPreview(null)}
          onConfirm={commit}
        />
      )}
    </div>
  );
}

function ValidationErrorsDialog({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pcp-excel-errors-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-[#F8D7DA]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#721C24]">Importação rejeitada</div>
            <h3 className="font-heading text-base font-bold text-[#721C24]">{data.errors.length} erro{data.errors.length === 1 ? "" : "s"} encontrados em {data.total} linha{data.total === 1 ? "" : "s"}</h3>
          </div>
          <button onClick={onClose} className="text-[#721C24] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-2 text-xs text-[#6C757D] border-b border-gray-100">
          Corrija o ficheiro Excel e tente novamente. Nada foi importado.
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left w-16">Linha</th>
                <th className="px-3 py-2 text-left">Coluna</th>
                <th className="px-3 py-2 text-left">Valor encontrado</th>
                <th className="px-3 py-2 text-left">Valor esperado</th>
              </tr>
            </thead>
            <tbody>
              {data.errors.map((e, i) => (
                <tr key={i} className="border-t border-gray-100" data-testid={`pcp-excel-error-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs">{e.row}</td>
                  <td className="px-3 py-2 text-xs text-[#1A1A2E]">{e.column}</td>
                  <td className="px-3 py-2 text-xs text-[#DC3545] font-mono break-all">{e.value}</td>
                  <td className="px-3 py-2 text-xs text-[#6C757D]">{e.expected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm" data-testid="pcp-excel-errors-close">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ data, committing, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pcp-excel-preview-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#1F3864] flex items-center gap-1">
              <FileSpreadsheet className="w-3 h-3" /> Pré-visualização
            </div>
            <h3 className="font-heading text-base font-bold text-[#1A1A2E]">
              {data.total} linha{data.total === 1 ? "" : "s"} prontas para importar
            </h3>
          </div>
          <button onClick={onCancel} className="text-[#6C757D] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-2 text-xs text-[#6C757D] border-b border-gray-100">
          Confirme os dados abaixo. Nada será gravado até clicar em <strong>Confirmar importação</strong>.
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Número da peça</th>
                <th className="px-3 py-2 text-left">Descrição</th>
                <th className="px-3 py-2 text-right">Qtd</th>
                <th className="px-3 py-2 text-left">Marca</th>
                <th className="px-3 py-2 text-left">Fonte</th>
                <th className="px-3 py-2 text-left">Notas</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100" data-testid={`pcp-excel-preview-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs text-[#1F3864]">{r.part_number}</td>
                  <td className="px-3 py-2 text-xs">{r.description}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.quantity}</td>
                  <td className="px-3 py-2 text-xs">{r.brand}</td>
                  <td className="px-3 py-2 text-xs">{r.source}</td>
                  <td className="px-3 py-2 text-xs text-[#6C757D]">{r.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={committing}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8] disabled:opacity-60"
            data-testid="pcp-excel-preview-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={committing}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60"
            data-testid="pcp-excel-preview-confirm"
          >
            {committing ? "A importar…" : `Confirmar importação (${data.total})`}
          </button>
        </div>
      </div>
    </div>
  );
}
